import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import type { TodosScope, TodosStatusFilter } from '@/lib/basecamp-view-persist'
import { testIds } from '@/lib/test-ids'

const PROJECT_OPTIONS: { id: TodosScope; label: string; title: string }[] = [
  { id: 'all', label: 'All projects', title: 'Hop * and client projects' },
  { id: 'hop_only', label: 'Hop projects', title: 'Projects named Hop *' },
  { id: 'client_only', label: 'Client projects', title: 'Projects not named Hop *' },
]

const DUE_OPTIONS: { id: TodosStatusFilter; label: string }[] = [
  { id: 'all', label: 'Any due date' },
  { id: 'overdue_and_today', label: 'Overdue + today' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'due_today', label: 'Due today' },
]

export function TodosFiltersMenu({
  scope,
  statusFilter,
  extraCount = 0,
  onScopeChange,
  onStatusChange,
  onReset,
}: {
  scope: TodosScope
  statusFilter: TodosStatusFilter
  extraCount?: number
  onScopeChange: (scope: TodosScope) => void
  onStatusChange: (status: TodosStatusFilter) => void
  onReset: () => void
}) {
  const chipCount =
    (scope !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + extraCount

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'h-7 gap-1 text-xs font-normal',
            chipCount > 0 && 'border-primary/50 text-primary',
          )}
          aria-label="More filters"
          data-testid={testIds.basecamp.todosFilters}
        >
          <SlidersHorizontal className="h-3 w-3" />
          Filters
          {chipCount > 0 ? (
            <span className="rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-foreground">
              {chipCount}
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS, 'w-52')}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
      >
        <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Projects
        </DropdownMenuLabel>
        {PROJECT_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.id}
            className="gap-2 text-xs"
            title={opt.title}
            onSelect={() => onScopeChange(opt.id)}
          >
            <Check className={cn('h-3.5 w-3.5', scope === opt.id ? 'opacity-100' : 'opacity-0')} />
            {opt.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Due
        </DropdownMenuLabel>
        {DUE_OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.id}
            className="gap-2 text-xs"
            onSelect={() => onStatusChange(opt.id)}
          >
            <Check
              className={cn(
                'h-3.5 w-3.5',
                statusFilter === opt.id ? 'opacity-100' : 'opacity-0',
              )}
            />
            {opt.label}
          </DropdownMenuItem>
        ))}
        {chipCount > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-xs text-muted-foreground" onSelect={onReset}>
              Reset filters
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
